import * as React from 'react'

import { Alert, Box, Icon, Text } from '@factorialco/deprecated-design-system'
import { type SelectOption } from '@factorialco/deprecated-design-system'
import { Form as FactorialForm } from 'factorial-form'
import { ImportContractsModalPageFragment } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import Select from 'modules/core/components/Inputs/Select'
import createEffect from 'modules/core/hooks/createEffect'
import i18n from 'modules/core/lib/i18n'
import { apiClient as ApiClient } from 'modules/core/lib/mobx-rest'

import useDownloadFile from 'modules/employees/hooks/useDownloadFile'

type BoxProps = React.ComponentProps<typeof Box>

class LegalEntityForm extends FactorialForm {
  constructor(values: object) {
    super(values, {
      legal_entity: 'string',
    })
  }
}
interface Props {
  options: SelectOption[]
  downloadPath: string
  importMode: string
  legalEntities: ImportContractsModalPageFragment[]
  checkDownload?: string
  allowEmptyDownload?: boolean
}

const checkDownloadability = createEffect(
  (
    legalEntity: ImportContractsModalPageFragment,
    setDownloadable: (downloadable: boolean) => void
  ) => {
    if (!legalEntity) return
    const employeeGroups = legalEntity.employeeGroups ?? []
    const downloadable = employeeGroups.some(
      (group) => group.employeeNamesConnection?.nodes.length > 0
    )

    setDownloadable(downloadable)
  }
)

const LegalEntitySelect = ({
  options,
  downloadPath,
  importMode,
  legalEntities,
  checkDownload,
  allowEmptyDownload = true,
}: Props) => {
  const api = React.useRef(ApiClient()).current
  const legalEntityForm = React.useRef(new LegalEntityForm({})).current
  const [legalEntityId, setLegalEntityId] = React.useState(options[0]?.value)
  const [downloadable, setDownloadable] = React.useState(true)

  const legalEntity = legalEntities.find((le) => le.id === legalEntityId)

  if (!allowEmptyDownload && legalEntities) {
    checkDownloadability(legalEntity, setDownloadable)
  }

  const { running: downloading, onSubmit: onDownloadRequest } = useDownloadFile({
    api: api,
    legalEntityId: legalEntityId,
    importMode: importMode,
    downloadPath: downloadPath,
    checkDownload: checkDownload,
  })

  const download: BoxProps['onClick'] = () => {
    if (downloadable && !downloading) {
      onDownloadRequest()
    }
  }

  return (
    <>
      {legalEntities.length > 1 && (
        <Select
          options={options}
          onChange={() => setLegalEntityId(legalEntityForm.get('legal_entity').value)}
          field={legalEntityForm.get('legal_entity')}
        />
      )}
      <Box
        cursor="pointer"
        width="full"
        gap="s4"
        padding="s16"
        flexDirection="row"
        alignItems="center"
        justifyContent="spaceBetween"
        onClick={download}
        border={{
          all: {
            width: 's1',
            style: 'solid',
            color: 'grey400',
          },
        }}
      >
        <Text.Normal size="200">
          {i18n.t('import_contract.download_template', {
            legal_entity: legalEntity?.legalName,
          })}
        </Text.Normal>
        <Box
          borderRadius={{
            all: 'rel1000',
          }}
          border={{
            all: {
              width: 's1',
              style: 'solid',
              color: 'grey400',
            },
          }}
        >
          <Icon.Medium icon="download" color="grey600" />
        </Box>
      </Box>
      {!downloadable && (
        <Alert color="error">
          <Box alignItems="flexStart" flexDirection="row" gap="s12">
            <Box flexNoShrink>
              <Icon.Medium icon="alert-circle" color="grey800" />
            </Box>
            <Text.Normal>{i18n.t('import_contract.legal_entity_with_no_employees')}</Text.Normal>
          </Box>
        </Alert>
      )}
    </>
  )
}

export default observer(LegalEntitySelect)
